import type { TransactionState } from '@framework/constants/enums/transaction';

import { type User } from '@type/members';

export enum AuthenticationStep {
    ChangeOneTouch = 'changeOneTouch',
    ForgotPasswordForm = 'forgotPasswordForm',
    GenericError = 'genericError',
    InvalidSecondaryMethod = 'invalidSecondaryMethod',
    LoginSuccessPrompt = 'loginSuccessPrompt',
    MtanEnroll = 'mtanEnroll',
    MtanProof = 'mtan',
    Onboarding = 'onboarding',
    OneTouchProof = 'one_touch',
    PrimaryProof = 'primaryProof',
    ProcessComplete = 'ProcessComplete',
    RemoveOneTouch = 'removeOneTouch',
    RemovePhone = 'removePhone',
    Request2FAResetLink = 'request2FAResetLink',
    SSOForm = 'ssoForm',
    SSOSelector = 'ssoSelector',
    SecondaryProofSelector = 'secondaryProofSelector',
    SetNewPassword = 'setNewPassword',
    SwitchAccount = 'switchAccount',
    TotpProof = 'totp'
}

// List of supported proof types from BE
export enum ProofType {
    Primary = 'primary',
    Secondary = 'secondary'
}

export enum PrimaryProofType {
    Password = 'password',
    SSO = 'sso'
}

export enum OtherPossibleLoginMethods {
    ContactSupport = 'contactSupport',
    Mtan = 'mtan',
    ResetFuturaeLink = 'resetFuturaeLink',
    ResetFuturaeWeb = 'resetFuturaeWeb'
}

export enum SecondaryProofType {
    Mtan = 'mtan',
    OneTouch = 'one_touch',
    Totp = 'totp'
}

export enum ProofState {
    Proven = 'proven'
}

type Device = {
    customName: string;
    id: string;
    name: string;
    state: string;
};

type Proof = {
    expiresIn: number;
    expiresInHuman: string;
    id: string;
    proves: string;
    state: ProofState;
    validFrom: string;
    validUntil: string;
};

type PrimaryProof = Proof & {
    acceptSecondary: SecondaryProofType[];
    requiresSecondary: boolean;
};

export type AuthenticationStatusPending = {
    id: string;
    noLongerProvableIn: number;
    noLongerProvableInHuman: string;
    provableFrom: string;
    provableUntil: string;
    proves: SecondaryProofType;
    state: 'pending';
};

export type AuthenticationStatus = {
    device: Device;
    isAuthenticated: boolean;
    pending: AuthenticationStatusPending[];
    acceptPrimary: SSOMethod[];
    primary?: PrimaryProof;
    secondary?: Proof;
    user?: Pick<User, 'email' | 'id' | 'phone'> & {
        name: string;
    };
};

export type AuthenticationStatusResponse = {
    proofStatus: AuthenticationStatus;
    results: {
        id: string;
        state: TransactionState;
    };
};

export type SSOMethod = {
    type: string;
    slug: string;
    url: string;
    name: string;
};

export enum AuthenticationErrorCode {
    Empty2faMethods = 'authentication:empty-2fa',
    InvalidEmail = 'authentication:invalid-email',
    InvalidEmailOrPassword = 'authentication:invalid-email-or-password',
    InvalidPassword = 'authentication:invalid-password',
    NoUsableDeviceEnrolled = 'authentication:futurae:no-usable-device-enrolled',
    ProofTypeNotSupported = 'authentication:proof-type-not-supported',
    SSOEmptyMethods = 'authentication:sso:empty-methods',
    ThrottleHours = 'authentication:throttle-hours',
    ThrottleMinutes = 'authentication:throttle-minutes',
    ThrottleSeconds = 'authentication:throttle-seconds',
    WrongMtanCode = 'authentication:mtan:wrong-code',
    WrongPassword = 'authentication:password:wrong-password',
    WrongTotpCode = 'authentication:totp:wrong-code'
}

export type SecondFactorStatusItem = {
    id: SecondaryProofType;
    isEnrolled: boolean;
    canEnroll: boolean;
    canUse: boolean;
};

export type SecondFactorStatusResponse = {
    id: string | null;
    expiresIn: number | null;
    canResetSecondFactor: boolean;
    secondFactors: SecondFactorStatusItem[];
};

export enum AuthenticationType {
    Password = 'password',
    SSO = 'sso',
    SecondaryFactor = 'secondaryFactor',
    Success = 'success'
}

export enum SecondaryAuthenticationType {
    Invalid = 'invalid',
    Mtan = 'mtan',
    OneTouch = 'one_touch',
    Request2FAResetLink = 'request2FAResetLink',
    SecondarySelector = 'secondarySelector',
    Totp = 'totp'
}

export enum SetNewPasswordStep {
    Default = 'Default',
    ProcessComplete = 'processComplete',
    SecondarySelector = 'secondarySelector'
}
